/**
 * The sign-in screen.
 *
 * Two doors: Google, which is how the team actually signs in, and a developer
 * door that only appears when the server reports it is enabled — so the
 * product can be run and shown before OAuth credentials exist, and can never
 * offer that door in production.
 */
import { useEffect, useState } from 'react'
import { ArrowRight, LogIn } from 'lucide-react'
import { api } from '@/store/api'
import { Button, Spinner, Avatar } from '@/components/ui/primitives'
import { initialsFor, type User } from '@lattice/core'

interface AuthConfig {
  google: boolean
  dev: boolean
  roster: { id: string; name: string; email: string }[]
}

export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [config, setConfig] = useState<AuthConfig>()
  const [busy, setBusy] = useState<string>()
  const [error, setError] = useState<string>()

  useEffect(() => {
    api.authConfig().then(setConfig).catch(e => setError(e.message))
  }, [])

  const devSignIn = async (email: string): Promise<void> => {
    setBusy(email)
    setError(undefined)
    try {
      await api.devSignIn(email)
      onSignedIn()
    } catch (e) {
      setError((e as Error).message)
      setBusy(undefined)
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-app px-6">
      <div className="w-full max-w-sm fade-in">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <Mark />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Lattice</h1>
            <p className="mt-1 text-sm text-[var(--fg-muted)]">
              Engineering drawings and CAD files for Bharat Atomic.
            </p>
          </div>
        </div>

        <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg-raised)] p-4 shadow-card">
          {!config && !error && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-[var(--fg-muted)]">
              <Spinner /> Checking how to sign you in…
            </div>
          )}

          {config?.google && (
            <Button asChild variant="primary" size="lg" className="w-full">
              <a href="/auth/login">
                <LogIn className="h-4 w-4" strokeWidth={1.75} />
                Continue with Google
              </a>
            </Button>
          )}

          {config?.dev && (
            <div className={config.google ? 'mt-4' : ''}>
              {config.google && (
                <div className="mb-3 flex items-center gap-2">
                  <div className="h-px flex-1 bg-[var(--border)]" />
                  <span className="text-xs text-[var(--fg-subtle)]">or, on this machine</span>
                  <div className="h-px flex-1 bg-[var(--border)]" />
                </div>
              )}
              <div className="space-y-0.5">
                {config.roster.map(person => (
                  <button
                    key={person.id}
                    onClick={() => void devSignIn(person.email)}
                    disabled={Boolean(busy)}
                    className="snap group flex w-full items-center gap-2.5 rounded-[var(--radius-md)] px-2 py-1.5 text-left hover:bg-[var(--bg-hover)] disabled:opacity-50"
                  >
                    <Avatar user={asUser(person)} size={24} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{person.name}</span>
                      <span className="block truncate text-xs text-[var(--fg-subtle)]">{person.email}</span>
                    </span>
                    {busy === person.email
                      ? <Spinner className="text-[var(--fg-subtle)]" />
                      : <ArrowRight className="h-3.5 w-3.5 shrink-0 text-[var(--fg-subtle)] opacity-0 snap group-hover:opacity-100" strokeWidth={1.75} />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {config && !config.google && !config.dev && (
            <p className="py-4 text-center text-sm text-[var(--fg-muted)]">
              Sign-in is not configured on this server yet.
            </p>
          )}

          {error && (
            <p className="mt-3 rounded-[var(--radius-sm)] border border-[color-mix(in_oklch,var(--deleted)_30%,transparent)] px-2 py-1.5 text-xs text-[var(--deleted)]">
              {error}
            </p>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-[var(--fg-subtle)]">
          Only @bharatatomic.com accounts on the Lattice roster can sign in.
        </p>
      </div>
    </div>
  )
}

/** The roster rows from /auth/config carry no avatar fields; derive them. */
function asUser(person: { id: string; name: string; email: string }): User {
  const index = ['jishnu', 'prerana', 'bhargava', 'sudipta', 'vishnukumar', 'atharva'].indexOf(person.id)
  return {
    id: person.id as User['id'],
    name: person.name,
    email: person.email,
    initials: initialsFor(person.name),
    accent: index < 0 ? 0 : index,
  }
}

/** The product mark: a lattice, drawn rather than imported. */
function Mark() {
  return (
    <svg width="40" height="40" viewBox="0 0 32 32" aria-hidden className="text-[var(--fg)]">
      <rect width="32" height="32" rx="8" fill="currentColor" opacity="0.06" />
      <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none">
        <path d="M7 11h18M7 16h18M7 21h18" opacity="0.9" />
        <path d="M12 6v20M20 6v20" opacity="0.35" />
      </g>
    </svg>
  )
}
